import { get } from 'lodash';
import join from 'lodash/fp/join';
import {
    InMemoryWebStorage,
    User,
    UserManager,
    UserManagerSettings,
    WebStorageStateStore,
} from 'oidc-client-ts';
import { OAuthConfigSettings } from 'src/main';

import { configureEnv, env } from '../../env';
import { configureStorage, RouteStorage } from './storage';
import {
    AuthenticatedUserWithProfile,
    MappedUserProfile,
    mapUserProfile,
    OICDUserProfile,
} from './userProfile';

const getWindow = () =>
    typeof window === 'undefined' ? ({} as Window & typeof globalThis) : window;

const param = (window: Window, regex: RegExp, defaultValue: string | null = null) => {
    let result = defaultValue;
    decodeURI(window.location.href).replace(regex, (_, it: string) => {
        result = it;
        return result;
    });
    return result;
};

const pullLocale = (result: AuthenticatedUserWithProfile) => get(result, 'profile.locale', 'en-GB');

export type UserPublishedInfo = {
    accessToken: string;
    expiresInSeconds: number;
    idToken: OICDUserProfile;
    locale: string;
    profile: MappedUserProfile;
};

const adaptPublishedInfo = (result: AuthenticatedUserWithProfile): UserPublishedInfo => {
    return {
        accessToken: result.access_token,
        expiresInSeconds: result.expires_in ?? 0,
        idToken: result.profile,
        locale: pullLocale(result),
        profile: mapUserProfile(result.profile),
    };
};

const configureAuth = (window: Window & typeof globalThis, importMetaEnv: ImportMetaEnv) => {
    const { runtimeConfig } = configureEnv(window, importMetaEnv);

    const redirectUri = runtimeConfig.login.redirectUri;
    const silentRedirectUri = runtimeConfig.login.silentRedirectUri;

    const settings: UserManagerSettings = {
        authority: `${runtimeConfig.login.authority}`,
        client_id: `${runtimeConfig.login.clientId}`,
        client_secret: `${runtimeConfig.login.clientId}`,
        monitorSession: true,
        staleStateAgeInSeconds: 600,
        filterProtocolClaims: false,
        loadUserInfo: false,
        redirect_uri: `${redirectUri}`,
        response_type: `code`,
        scope: join(' ', runtimeConfig.login.oauthScope).trim() || 'openid',
        silent_redirect_uri: `${silentRedirectUri || redirectUri}`,
        includeIdTokenInSilentRenew: false,
        automaticSilentRenew: true,
        userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    };

    return new UserManager(settings);
};

export const configureSetupOAuth = (
    auth: UserManager,
    storage: RouteStorage,
    window: Window & typeof globalThis,
    importMetaEnv: ImportMetaEnv,
) => {
    const { runtimeConfig } = configureEnv(window, importMetaEnv);

    const isFreshRedirect = Boolean(param(window, /.*code=([^&]+)/u));

    const saveCurrentRoute = () => {
        const initialRoute = [window.location.hash, window.location.search]
            .join('')
            .replace(/^#/u, '');

        storage.saveRoute(initialRoute);
    };

    return (config: OAuthConfigSettings) => {
        const trySignin = () =>
            auth
                .signinSilent()
                .then((result: User | null) => {
                    if (result === null) {
                        throw new Error('Silent signin returned no user');
                    }

                    config.onTokenRenewed(
                        adaptPublishedInfo({ ...result, expires_in: result.expires_in ?? 0 }),
                    );

                    if (!isFreshRedirect) {
                        saveCurrentRoute();
                    }

                    return result;
                })
                .catch((error: Error) => {
                    if (!isFreshRedirect) {
                        saveCurrentRoute();
                    }

                    config.onTokenExpired();
                    return Promise.reject(error);
                });

        auth.events.addAccessTokenExpiring(() => {});

        auth.events.addAccessTokenExpired(() => {
            config.onTokenExpired();
        });

        auth.events.addSilentRenewError((error) => {
            config.onSessionError(error);
            config.onTokenExpired();
        });

        auth.events.addUserLoaded((user: User) => {
            config.onTokenRenewed(
                adaptPublishedInfo({ ...user, expires_in: user.expires_in ?? 0 }),
            );
        });

        auth.events.addUserSignedOut(() => {
            config.onTokenExpired();
        });

        return trySignin().catch((error) => {
            const mightBeSuspicious = isFreshRedirect;

            if (runtimeConfig.login.preventRedirect) {
                // eslint-disable-next-line no-console
                console.warn('[feature/login] redirect prevented due to config. Error was', error);
            } else if (mightBeSuspicious) {
                storage.discardRoute();
                config.onSessionError(error);
            } else {
                saveCurrentRoute();
                auth.signinRedirect();
            }

            return Promise.reject(new Error(`Need to sign in`));
        });
    };
};

const runtimeAuth = configureAuth(window, import.meta.env);

export const setupOAuth = configureSetupOAuth(
    runtimeAuth,
    configureStorage(getWindow()),
    getWindow(),
    import.meta.env,
);

export const mockOAuth = ({
    onTokenRenewed,
}: {
    onTokenRenewed: (result: UserPublishedInfo) => unknown;
}) => {
    // eslint-disable-next-line no-console
    console.warn(`[feature/login/oidc-session] Using mocked authorization due to config setting`);

    onTokenRenewed(
        adaptPublishedInfo({
            access_token:
                (env.featureToggles.mockedToken as string) || 'valid-mocked-oauth-bogus-token',
            expires_in: 60 * 60 * 24 * 365,
            scope: 'openid',
            profile: {
                locale: env.runtimeConfig.login.mockLocale,
                account: '4c8e1f07-8510-4bbf-79ca-9d706675b9da',
            },
        }),
    );

    return Promise.resolve();
};

export const configureRetrieveInitialState = (storage: RouteStorage) => () => ({
    initialRoute: storage.getRoute(),
});

export const retrieveInitialState = configureRetrieveInitialState(configureStorage(getWindow()));
